/* eslint-disable @typescript-eslint/no-unused-vars */
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../../../hooks/useQuery';
import ConsultsService from '../../../../../services/ConsultsService';
import FilterRadioButton from '../../../../FilterRadioButtons';
import MyModal from '../../../../Modal';
import { ICreateInstructionApiResponse, ISelectedCopyOption, ListaItensVt, ListaTrajetosCopum, TrajetoInstruc } from '../types';

interface IAbateResultModal {
  copyModalShow: boolean;
  setCopyModalShow: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setInstructions: Dispatch<SetStateAction<TrajetoInstruc[]>>;
  direction: 'going' | 'returning';
  copyOptions: ListaTrajetosCopum[];
  manualRoutingType:  'detailed' | 'simple';
  instructionsSeparatedByDirection: { going: TrajetoInstruc[], returning: TrajetoInstruc[] };
  vtItensOptions: ListaItensVt[];
}

export default function CopyModal({
  copyModalShow,
  setCopyModalShow,
  setInstructions,
  setIsLoading,
  direction,
  copyOptions,
  manualRoutingType,
  instructionsSeparatedByDirection,
  vtItensOptions,
}: IAbateResultModal) {
  const [selectedCopyOption, setSelectedCopyOption] = useState({} as ISelectedCopyOption);
  const [willCopyManualInverseDirection, setWillCopyManualInverseDirection] = useState(false);

  const { apiCall } = useApiCall();
  const { userCode, userProfile } = useGetGeneralAppData();
  const query = useQuery();
  const companyCode = query.get('codEmpresa');
  const { consultCode } = useParams();

  const mappedCopyOptions = copyOptions?.map((x) => ({
    id: `${x.codSentidoOrigem} ${x.codSentidoDestino} ${x.rotaDestino} ${x.codSentidoDestino} ${x.trajetoDestino}`,
    label: `${x.descrSentidoOrigem}${x.descrOpcaoTrajeto}`,
    codSentidoOrigem: direction === 'going' ? 1 : 2,
    rotaDestino: x.rotaDestino,
    codSentidoDestino: x.codSentidoDestino,
    trajetoDestino: x.trajetoDestino,
  })) || [];

  const copyResult = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.copyResultToManualRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        codConsulta: consultCode,
        codSentidoOrigem: direction === 'going' ? 1 : 2,
        rotaDestino: selectedCopyOption.rotaDestino,
        codSentidoDestino: selectedCopyOption.codSentidoDestino,
        trajetoDestino: selectedCopyOption.trajetoDestino,
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a cópia. Por favor, tente novamente',
      actionAfterResponse: (response: ICreateInstructionApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a cópia. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setCopyModalShow(false);
        toast.success(`O resultado foi copiado para a ${direction === 'going' ? 'ida' : 'volta'} com sucesso. Conclua a roteirização, ou adicione mais instruções!`);
        const instructionsByManualRoutingTypeSelected = manualRoutingType === 'detailed' ? response.trajetoInstrucoes : response.trajetoInstrucoes.filter((ist) => ist.instrucao === 'Transporte Público').map((ist) => ({
          ...ist,
          origem: 'Local indefinido',
          destino: 'Local indefinido',
          routeID: '',
          tempo: 30,
          distancia: 2000,
          origemLat: 0,
          origemLng: 0,
          destinoLat: 0,
          destinoLng: 0,
        }));

        setInstructions(instructionsByManualRoutingTypeSelected);
      }
    });
  }, [apiCall, userCode, userProfile, companyCode, consultCode, direction, selectedCopyOption.rotaDestino, selectedCopyOption.codSentidoDestino, selectedCopyOption.trajetoDestino, setIsLoading, setCopyModalShow, manualRoutingType, setInstructions]);

  const copyManualInverseDirection = useCallback(async () => {
    const inverseDirectionInstructions = direction === 'going' ? instructionsSeparatedByDirection.returning : instructionsSeparatedByDirection.going;
    const reversedArray = [...inverseDirectionInstructions].reverse();
    const reorderedSequence = reversedArray.map((x, i) => ({ ...x, sequencia: i + 1 }));
    const inversedOriginDestinyDirection = reorderedSequence.map((x) => ({ ...x, origem: x.destino, destino: x.origem, origemLat: x.destinoLat, origemLng: x.destinoLng, destinoLat: x.origemLat, destinoLng: x.origemLng, codSentido: direction === 'returning' ? 2 : 1, sentido: direction === 'returning' ? 'VOLTA - TRABALHO-CASA' : 'IDA - CASA-TRABALHO'
    }));

    const findCorrespondingVtItem = (vtItemCode: number) => (
      vtItensOptions.find((vt) => vt.codItem === vtItemCode)
    );

    const instructionToCodeLiteral: { [key: string]: number } = {
      'Transporte': 2,
      'Transporte Público': 2,
      'Caminhar': 1,
      'Caminhada': 1,
      'Abater Resultado': 3
    };

    const mapInstructions = (instruction: TrajetoInstruc[]) => {
      const mappedCurrentInstructions = (!instruction || instruction?.length === 0) ? [] : instruction.map((inst) => ({
        tipoInstrucao: instructionToCodeLiteral[inst.instrucao],
        instrucao: inst.instrucao,
        sequencia: inst.sequencia,
        origem: inst.origem,
        destino: inst.destino,
        linhaTransporte: inst.linhaTransp,
        routeID: '',
        tempo: inst.tempo,
        distancia: inst.distancia,
        origemLat: inst.origemLat,
        origemLng: inst.origemLng,
        destinoLat: inst.destinoLat,
        destinoLng: inst.destinoLng,
        codGeo: findCorrespondingVtItem(inst.codItem)?.codGeo,
        codItem: inst.codItem,
        tarifa: findCorrespondingVtItem(inst.codItem)?.tarifa,
      }));

      return mappedCurrentInstructions;
    };

    await apiCall({
      apiToCall: ConsultsService.createInstruction,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        codConsulta: consultCode,
        rota: 1,
        sentido: direction === 'going' ? 1 : 2,
        trajetoInstrucoes: mapInstructions(inversedOriginDestinyDirection),
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a cópia. Por favor, tente novamente',
      actionAfterResponse: (response: ICreateInstructionApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a cópia. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setCopyModalShow(false);
        toast.success('A cópia foi realizada com sucesso. Conclua a roteirização, ou adicione mais instruções!');
        setInstructions(response.trajetoInstrucoes);
      }
    });
  }, [apiCall, companyCode, consultCode, direction, instructionsSeparatedByDirection.going, instructionsSeparatedByDirection.returning, setCopyModalShow, setInstructions, setIsLoading, userCode, userProfile, vtItensOptions]);

  return (
    <MyModal
      show={copyModalShow}
      title={`Copiar Resultado Para (${direction === 'going' ? 'Ida' : 'Volta'})`}
      closeButtonLabel="Fechar"
      actionButtonLabel="Copiar"
      onClose={() => setCopyModalShow(false)}
      onAction={() => willCopyManualInverseDirection ? copyManualInverseDirection() : copyResult()}
      isActionButtonDisabled={!selectedCopyOption.id && !willCopyManualInverseDirection}
      type="action"
      size='lg'
      modalBody={(
        <>
          <h6>Selecione qual a opção de trajeto que você deseja copiar para a {direction === 'going' ? 'Ida' : 'Volta'}</h6>
          <Container>
            <Row  xs={1} md={2} lg={3}>
              {mappedCopyOptions?.length > 0 && mappedCopyOptions.map((x) => (
                <Col key={x.id}>
                  <FilterRadioButton selected={(x.id === selectedCopyOption.id) && !willCopyManualInverseDirection} onClick={() => {
                    setSelectedCopyOption(x);
                    setWillCopyManualInverseDirection(false);
                  }}>
                    {x.label}
                  </FilterRadioButton>
                </Col>
              ))}
              {direction === 'returning' && instructionsSeparatedByDirection.going.length > 0 && (
                <Col>
                  <FilterRadioButton selected={willCopyManualInverseDirection} onClick={() => setWillCopyManualInverseDirection(true)}>
                      IDA - (ROT MANUAL ATUAL)
                  </FilterRadioButton>
                </Col>
              )}
              {direction === 'going' && instructionsSeparatedByDirection.returning.length > 0 && (
                <Col>
                  <FilterRadioButton selected={willCopyManualInverseDirection} onClick={() => setWillCopyManualInverseDirection(true)}>
                      VOLTA - (ROT MANUAL ATUAL)
                  </FilterRadioButton>
                </Col>
              )}
              {direction === 'returning' && instructionsSeparatedByDirection.going.length > 0 && (
                <Col>
                  <FilterRadioButton selected={willCopyManualInverseDirection} onClick={() => setWillCopyManualInverseDirection(true)}>
                      IDA - (ROT MANUAL ATUAL)
                  </FilterRadioButton>
                </Col>
              )}
            </Row>
          </Container>
        </>
      )}
    />
  );
}
